import { useState } from "react";
import { Link, useMatch } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Icon, type IconName } from "@/components/ui/Icon";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ROUTES } from "@/constants/routes";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/cn";

interface NavItem {
  label: string;
  path: string;
  icon: IconName;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Боты", path: ROUTES.admin.bots, icon: "robot" },
  { label: "Конфиги", path: ROUTES.admin.botConfigs, icon: "file-pen-line" },
  { label: "Наборы", path: ROUTES.admin.coinSets, icon: "diamond" },
  { label: "Воркеры", path: ROUTES.admin.workers, icon: "grid" },
  { label: "Юзеры", path: ROUTES.admin.users, icon: "users" },
  { label: "Продукт", path: ROUTES.admin.productAnalytics, icon: "chart-network" },
];

const NavButton = ({ path, icon, label }: NavItem) => {
  const isActive = useMatch(path);

  return (
    <Button
      asChild
      variant={isActive ? "secondary" : "neutral"}
      size="sm"
    >
      <Link to={path}>
        <Icon name={icon} />
        {label}
      </Link>
    </Button>
  );
};

// ============ MobileNav ============

interface MobileNavItemProps extends NavItem {
  onNavigate: () => void;
}

const MobileNavItem = ({ path, icon, label, onNavigate }: MobileNavItemProps) => {
  const isActive = useMatch(path);

  return (
    <Button
      asChild
      variant={isActive ? "secondary" : "neutral"}
      size="md"
      className="w-full justify-start"
    >
      <Link
        to={path}
        onClick={onNavigate}
      >
        <Icon name={icon} />
        {label}
      </Link>
    </Button>
  );
};

const MobileNav = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label="Открыть меню"
        className={cn(
          "fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-50",
          "flex size-12 items-center justify-center rounded-full",
          "glass-blur bg-neutral-200/10 text-neutral-50",
          "shadow-lg transition-transform active:scale-95"
        )}
      >
        <Icon
          name="grip-vertical"
          className="size-5"
        />
      </button>

      <Sheet
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <SheetContent
          side="bottom"
          className="rounded-t-mds gap-3 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
        >
          <SheetHeader className="p-0">
            <SheetTitle>Меню</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-2">
            {NAV_ITEMS.map((item) => (
              <MobileNavItem
                key={item.path}
                {...item}
                onNavigate={() => setIsOpen(false)}
              />
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

// ============ DesktopNav ============

const DesktopNav = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <nav
      className={cn(
        // Position: fixed at bottom center, 1rem from bottom
        "fixed bottom-4 left-1/2 z-50 -translate-x-1/2",
        // Layout
        "flex items-center gap-2 p-2",
        // Style
        "glass-blur rounded-2xl bg-neutral-200/5",
        // Transition
        "transition-transform duration-300",
        isCollapsed && "translate-y-[calc(100%+1rem)]"
      )}
    >
      {/* Toggle button - visible when collapsed */}
      <Button
        variant="neutral"
        buttonType="icon"
        size="sm"
        onClick={() => setIsCollapsed(false)}
        className={cn(
          "absolute -top-12 left-1/2 -translate-x-1/2",
          "transition-opacity duration-300",
          isCollapsed ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      >
        <Icon name="angle-up" />
      </Button>

      {/* Nav items */}
      {NAV_ITEMS.map((item) => (
        <NavButton
          key={item.path}
          {...item}
        />
      ))}

      {/* Collapse button */}
      <Button
        variant="neutral"
        buttonType="icon"
        size="sm"
        onClick={() => setIsCollapsed(true)}
      >
        <Icon name="angle-down" />
      </Button>
    </nav>
  );
};

// ============ AdminBottomNav ============

export const AdminBottomNav = () => {
  const isMobile = useIsMobile();

  return isMobile ? <MobileNav /> : <DesktopNav />;
};
